import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useServerConfig } from '../context/ServerConfigContext';
import { createApiService } from '../utils/apiService';
import { MdOpenInNew, MdCheckCircle, MdCancel } from 'react-icons/md';
import './StockReportPage.css';

const StockReportPage = () => {
  const { getServer } = useServerConfig();
  const [activeTab, setActiveTab] = useState('reports');
  const [summaries, setSummaries] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [readIds, setReadIds] = useState(new Set());
  const observerTarget = useRef(null);
  const loadingRef = useRef(false);

  // Filter states
  const [filters, setFilters] = useState({
    q: '',
    tags: '',
    researchers: '',
    themes: '',
    securities_from: '',
    date_from: '',
    date_to: ''
  });
  const [filterExpanded, setFilterExpanded] = useState(false);

  // Statistics states
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsDateFrom, setStatsDateFrom] = useState('');
  const [statsDateTo, setStatsDateTo] = useState('');
  const [statsTimeFrom, setStatsTimeFrom] = useState('');
  const [statsTimeTo, setStatsTimeTo] = useState('');

  const PAGE_SIZE = 20;
  const READ_ITEMS_KEY = 'stock-report-read-items';

  // Extract filename from {message_id}_{filename} format
  const extractFilename = (fullName) => {
    if (!fullName) return '';
    const parts = fullName.split('_');
    if (parts.length > 1) {
      return parts.slice(1).join('_');
    }
    return fullName;
  };

  // Build query string from filters
  const buildQueryString = useCallback((pageNum, customFilters = null) => {
    const params = new URLSearchParams();
    params.append('page', pageNum);
    params.append('page_size', PAGE_SIZE);

    // Use custom filters if provided, otherwise use current filters
    const filtersToUse = customFilters || filters;

    // Add filters
    Object.entries(filtersToUse).forEach(([key, value]) => {
      if (value && value.trim()) {
        params.append(key, value.trim());
      }
    });

    return params.toString();
  }, [filters]);

  // Fetch documents
  const fetchSummaries = useCallback(async (pageNum, customFilters = null) => {
    if (loadingRef.current) {
      console.log('[Fetch] Already loading, skipping page:', pageNum);
      return;
    }

    const filtersToUse = customFilters || filters;
    console.log('[Fetch] Requesting page:', pageNum, 'with filters:', filtersToUse);
    loadingRef.current = true;
    setLoading(true);

    try {
      const server = getServer('stock-report-api-server');
      if (!server || !server.isActive) {
        console.error('stock-report-api-server를 찾을 수 없습니다.');
        return;
      }

      const apiService = createApiService(server);

      // Build params
      const params = {};
      params.page = pageNum;
      params.page_size = PAGE_SIZE;

      // Add filters
      Object.entries(filtersToUse).forEach(([key, value]) => {
        if (value && value.trim()) {
          params[key] = value.trim();
        }
      });

      console.log('📊 [StockReportPage] API 요청:', params);

      const response = await apiService.get('/api/v1/documents', params);

      console.log('📥 [StockReportPage] 원본 응답:', response);

      if (response.data && response.data.length > 0) {
        setSummaries(prev => pageNum === 1 ? response.data : [...prev, ...response.data]);
        setHasMore(response.page < response.total_pages);
        console.log('✅ 문서 설정 완료:', response.data.length, '개');
      } else {
        if (pageNum === 1) {
          setSummaries([]);
          console.log('⚠️ 데이터 없음');
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error('Failed to fetch summaries:', error);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [filters, getServer, buildQueryString]);

  // Load read items from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(READ_ITEMS_KEY);
      if (stored) {
        setReadIds(new Set(JSON.parse(stored)));
      }
    } catch (error) {
      console.error('Failed to load read items:', error);
    }
  }, []);

  // Set default stats date range (24 hours ago ~ now)
  useEffect(() => {
    const now = new Date();
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    // Format to YYYY-MM-DD
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Format to HH:MM
    const formatTime = (date) => {
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    };

    setStatsDateFrom(formatDate(yesterday));
    setStatsDateTo(formatDate(now));
    setStatsTimeFrom(formatTime(yesterday));
    setStatsTimeTo(formatTime(now));
  }, []);

  // Initial load and when filters change
  useEffect(() => {
    console.log('[Init/Filters] Loading page 1 with filters:', filters);
    setSummaries([]);
    setPage(1);
    setHasMore(true);
    fetchSummaries(1);
  }, [fetchSummaries]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loading && activeTab === 'reports') {
          console.log('[Observer] Triggering next page');
          setPage(prev => {
            console.log('[Observer] Page increment:', prev, '->', prev + 1);
            return prev + 1;
          });
        }
      },
      { threshold: 1.0 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, activeTab]);

  // Fetch next page when page changes
  useEffect(() => {
    console.log('[Effect] Page changed to:', page);
    if (page > 1) {
      fetchSummaries(page);
    }
  }, [page, fetchSummaries]);

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
        markAsRead(id);
      }
      return newSet;
    });
  };

  const markAsRead = (id) => {
    setReadIds(prev => {
      const newSet = new Set(prev);
      newSet.add(id);
      try {
        localStorage.setItem(READ_ITEMS_KEY, JSON.stringify([...newSet]));
      } catch (error) {
        console.error('Failed to save read items:', error);
      }
      return newSet;
    });
  };

  const getPdfViewUrl = (id) => {
    if (!id) return '#';
    return `/stock-report-api/api/v1/pdfs/${id}/view`;
  };

  const handleFilterChange = (field) => (event) => {
    const value = event.target.value;
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilters = () => {
    console.log('[Filter] Applying filters:', filters);
    setSummaries([]);
    setPage(1);
    setHasMore(true);
    fetchSummaries(1);
  };

  const handleResetFilters = () => {
    setFilters({
      q: '',
      tags: '',
      researchers: '',
      themes: '',
      securities_from: '',
      date_from: '',
      date_to: ''
    });
    setSummaries([]);
    setPage(1);
    setHasMore(true);
  };

  // Add filter value and trigger search
  const handleAddFilter = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setFilterExpanded(true);
    // Auto-apply filter
    setTimeout(() => {
      setSummaries([]);
      setPage(1);
      setHasMore(true);
      fetchSummaries(1);
    }, 100);
  };

  // Navigate to PDF reports tab with filter (from statistics page)
  const handleFilterAndNavigate = (field, value) => {
    // Set filter and switch to reports tab
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setFilterExpanded(true);
    setActiveTab('reports');

    // Auto-trigger search after tab switch
    setTimeout(() => {
      handleApplyFilters();
    }, 150);
  };

  // Statistics fetch
  const fetchStats = async () => {
    if (!statsDateFrom || !statsDateTo || !statsTimeFrom || !statsTimeTo) {
      alert('조회 기간과 시간을 모두 입력하세요.');
      return;
    }

    setStatsLoading(true);
    try {
      const server = getServer('stock-report-api-server');
      if (!server || !server.isActive) return;

      const apiService = createApiService(server);
      const params = {
        time_from: `${statsDateFrom} ${statsTimeFrom}:00`,
        time_to: `${statsDateTo} ${statsTimeTo}:00`
      };

      const response = await apiService.get('/api/v1/documents/tag-theme-stats', params);
      setStats(response.data || response);
    } catch (error) {
      console.error('통계 조회 실패:', error);
      alert('통계 조회에 실패했습니다.');
    } finally {
      setStatsLoading(false);
    }
  };

  return (
    <div className="stock-report-page">
      <div className="stock-report-header">
        <h1>📊 증권리포트</h1>
        <div className="tab-buttons">
          <button
            className={`tab-button ${activeTab === 'reports' ? 'active' : ''}`}
            onClick={() => setActiveTab('reports')}
          >
            📄 PDF 리포트
          </button>
          <button
            className={`tab-button ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            📊 통계 분석
          </button>
        </div>
      </div>

      {/* PDF Reports Tab */}
      {activeTab === 'reports' && (
        <div className="reports-tab">
          {/* Filter Section */}
          <div className="filter-section">
            <div
              className="filter-header"
              onClick={() => setFilterExpanded(!filterExpanded)}
            >
              <span>🔍 필터 & 검색</span>
              <span className="expand-icon">{filterExpanded ? '▲' : '▼'}</span>
            </div>
            {filterExpanded && (
              <div className="filter-content">
                <div className="filter-grid">
                  <input
                    type="text"
                    placeholder="검색 (파일명 또는 요약)"
                    value={filters.q}
                    onChange={handleFilterChange('q')}
                    className="filter-input full-width"
                  />
                  <input
                    type="text"
                    placeholder="태그"
                    value={filters.tags}
                    onChange={handleFilterChange('tags')}
                    className="filter-input"
                  />
                  <input
                    type="text"
                    placeholder="리서처"
                    value={filters.researchers}
                    onChange={handleFilterChange('researchers')}
                    className="filter-input"
                  />
                  <input
                    type="text"
                    placeholder="테마"
                    value={filters.themes}
                    onChange={handleFilterChange('themes')}
                    className="filter-input"
                  />
                  <input
                    type="text"
                    placeholder="증권사"
                    value={filters.securities_from}
                    onChange={handleFilterChange('securities_from')}
                    className="filter-input"
                  />
                  <input
                    type="date"
                    placeholder="메시지 시작일"
                    value={filters.date_from}
                    onChange={handleFilterChange('date_from')}
                    className="filter-input"
                  />
                  <input
                    type="date"
                    placeholder="메시지 종료일"
                    value={filters.date_to}
                    onChange={handleFilterChange('date_to')}
                    className="filter-input"
                  />
                </div>
                <div className="filter-actions">
                  <button onClick={handleResetFilters} className="filter-btn reset-btn">
                    초기화
                  </button>
                  <button onClick={handleApplyFilters} className="filter-btn search-btn">
                    검색
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Documents List */}
          <div className="documents-list">
            {summaries.map((summary) => {
              const isExpanded = expandedIds.has(summary.id);
              const isRead = readIds.has(summary.id);
              return (
                <div
                  key={summary.id}
                  className={`document-card ${isRead ? 'read' : ''}`}
                  onClick={() => toggleExpand(summary.id)}
                >
                  <div className="document-header">
                    <span className="document-icon">📄</span>
                    <span className="document-name">{extractFilename(summary.file_name)}</span>
                    <div className="document-actions">
                      {/* Summary status indicator */}
                      {!isExpanded && (
                        summary.summary_text ? (
                          <MdCheckCircle className="summary-indicator has-summary" title="요약 있음" />
                        ) : (
                          <MdCancel className="summary-indicator no-summary" title="요약 없음" />
                        )
                      )}
                      <a
                        href={getPdfViewUrl(summary.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="pdf-link"
                        title="PDF 원문 보기"
                      >
                        <MdOpenInNew />
                      </a>
                      <span className="expand-icon">{isExpanded ? '▲' : '▼'}</span>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="document-details" onClick={(e) => e.stopPropagation()}>
                      {summary.securities_firm && (
                        <div className="detail-row">
                          <strong>증권사:</strong>
                          <span
                            className="tag clickable"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddFilter('securities_from', summary.securities_firm);
                            }}
                            title="클릭하여 필터 추가"
                          >
                            {summary.securities_firm}
                          </span>
                        </div>
                      )}
                      {summary.tags && summary.tags.length > 0 && (
                        <div className="detail-row">
                          <strong>태그:</strong>
                          {summary.tags.map((tag, idx) => (
                            <span
                              key={idx}
                              className="tag clickable"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddFilter('tags', tag);
                              }}
                              title="클릭하여 필터 추가"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      {summary.researchers && summary.researchers.length > 0 && (
                        <div className="detail-row">
                          <strong>리서처:</strong>
                          {summary.researchers.map((researcher, idx) => (
                            <span
                              key={idx}
                              className="tag clickable researcher"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddFilter('researchers', researcher);
                              }}
                              title="클릭하여 필터 추가"
                            >
                              {researcher}
                            </span>
                          ))}
                        </div>
                      )}
                      {summary.themes && summary.themes.length > 0 && (
                        <div className="detail-row">
                          <strong>테마:</strong>
                          {summary.themes.map((theme, idx) => (
                            <span
                              key={idx}
                              className="tag clickable theme"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddFilter('themes', theme);
                              }}
                              title="클릭하여 필터 추가"
                            >
                              {theme}
                            </span>
                          ))}
                        </div>
                      )}
                      {summary.summary_text ? (
                        <div className="summary-text">
                          {summary.summary_text}
                        </div>
                      ) : summary.message_text ? (
                        <div className="summary-text message-text-fallback">
                          {summary.message_text}
                        </div>
                      ) : null}
                      {summary.message_date && (
                        <div className="detail-info">
                          📅 메시지 날짜: {new Date(summary.message_date).toLocaleString('ko-KR')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {!loading && summaries.length === 0 && (
            <div className="empty-state">
              <p>데이터가 없습니다.</p>
            </div>
          )}

          <div ref={observerTarget} style={{ height: '20px' }} />
        </div>
      )}

      {/* Statistics Tab */}
      {activeTab === 'stats' && (
        <div className="stats-tab">
          <div className="stats-filter">
            <h3>📅 조회 기간 설정</h3>
            <div className="stats-filter-grid">
              <div className="date-time-group">
                <label>시작</label>
                <div className="date-time-inputs">
                  <input
                    type="date"
                    value={statsDateFrom}
                    onChange={(e) => setStatsDateFrom(e.target.value)}
                    className="filter-input"
                  />
                  <input
                    type="time"
                    value={statsTimeFrom}
                    onChange={(e) => setStatsTimeFrom(e.target.value)}
                    className="filter-input"
                  />
                </div>
              </div>
              <div className="date-time-group">
                <label>종료</label>
                <div className="date-time-inputs">
                  <input
                    type="date"
                    value={statsDateTo}
                    onChange={(e) => setStatsDateTo(e.target.value)}
                    className="filter-input"
                  />
                  <input
                    type="time"
                    value={statsTimeTo}
                    onChange={(e) => setStatsTimeTo(e.target.value)}
                    className="filter-input"
                  />
                </div>
              </div>
              <button
                onClick={fetchStats}
                disabled={statsLoading}
                className="filter-btn search-btn"
              >
                {statsLoading ? '조회 중...' : '통계 조회'}
              </button>
            </div>
          </div>

          {stats && (
            <div className="stats-content">
              <div className="stats-summary">
                <div className="stat-card">
                  <div className="stat-label">전체 문서</div>
                  <div className="stat-value">{stats.total_documents}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">태그 종류</div>
                  <div className="stat-value">{Object.keys(stats.tags || {}).length}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">테마 종류</div>
                  <div className="stat-value">{Object.keys(stats.themes || {}).length}</div>
                </div>
              </div>

              <div className="stats-charts">
                <div className="chart-section">
                  <h3>🏆 Top 20 태그</h3>
                  {stats.top_tags && stats.top_tags.map((item, idx) => (
                    <div
                      key={idx}
                      className="bar-chart-item clickable"
                      onClick={() => handleFilterAndNavigate('tags', item.name)}
                      title="클릭하여 이 태그로 필터링된 리포트 보기"
                    >
                      <div className="bar-label">{item.name}</div>
                      <div className="bar-container">
                        <div
                          className="bar"
                          style={{ width: `${(item.count / stats.top_tags[0].count) * 100}%` }}
                        >
                          <span className="bar-value">{item.count}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="chart-section">
                  <h3>🏆 Top 20 테마</h3>
                  {stats.top_themes && stats.top_themes.map((item, idx) => (
                    <div
                      key={idx}
                      className="bar-chart-item clickable"
                      onClick={() => handleFilterAndNavigate('themes', item.name)}
                      title="클릭하여 이 테마로 필터링된 리포트 보기"
                    >
                      <div className="bar-label">{item.name}</div>
                      <div className="bar-container">
                        <div
                          className="bar"
                          style={{ width: `${(item.count / stats.top_themes[0].count) * 100}%` }}
                        >
                          <span className="bar-value">{item.count}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {!stats && !statsLoading && (
            <div className="empty-state">
              <p>기간을 선택하고 통계를 조회하세요</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StockReportPage;
